<template>
  <div class="auction_view">
    <!-- Loading -->
    <div v-if="!loaded && !error" class="state_box">
      <div class="spinner"></div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state_box">
      <div class="error_card">
        <p>{{ error }}</p>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="page_content">
      <!-- Top header -->
      <div class="top_header">
        <div class="header_inner">
          <button class="back_btn" @click="goBack">← Wróć do wyników</button>
          <div class="badge_row">
            <span class="badge badge_jdm">JDM</span>
            <span
              class="badge"
              :class="repaired ? 'badge_warn' : 'badge_rating'"
              v-if="car.auction_rating"
            >Ocena {{ car.auction_rating }}</span>
            <span class="badge badge_trans" v-if="car.gear_type">{{ gearLabel }}</span>
            <span class="badge badge_trans" v-if="car.steering_wheel">
              {{ car.steering_wheel === 'rhd' ? 'Kierownica prawa' : 'Kierownica lewa' }}
            </span>
          </div>
          <h1>{{ car.maker }} {{ car.model }}, {{ car.year }}</h1>
          <div class="info_strip">
            <span class="info_chip">
              <span class="chip_label">Lot</span>
              <strong>{{ orDash(car.lot) }}</strong>
            </span>
            <span class="info_chip">
              <span class="chip_label">Nr nadwozia</span>
              <strong class="mono">{{ orDash(car.vin_number) }}</strong>
            </span>
            <span class="info_chip">
              <span class="chip_label">Dom aukcyjny</span>
              <strong>{{ orDash(car.auction_location) }}</strong>
            </span>
            <span class="info_chip">
              <span class="chip_label">Przebieg</span>
              <strong>{{ formatKm(car.mileage) }}</strong>
            </span>
            <span class="info_chip">
              <span class="chip_label">Silnik</span>
              <strong>{{ formatCc(car.engine_volume) }}</strong>
            </span>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div class="body">
        <!-- Left: gallery + vehicle data -->
        <div class="left_col">
          <!-- Gallery -->
          <div class="gallery_wrap">
            <div class="main_image_wrap">
              <img :src="photos[currentIndex]" @error="imageError" class="main_img" />
              <template v-if="photos.length > 1">
                <button class="arrow arrow_l" @click="prev">&#8249;</button>
                <button class="arrow arrow_r" @click="next">&#8250;</button>
                <span class="counter">{{ currentIndex + 1 }} / {{ photos.length }}</span>
              </template>
            </div>
            <div class="thumbs" v-if="photos.length > 1">
              <img
                v-for="(p, i) in photos"
                :key="i"
                :src="p"
                @error="imageError"
                @click="currentIndex = i"
                :class="{ active: i === currentIndex }"
                class="thumb"
              />
            </div>
          </div>

          <!-- Vehicle specs -->
          <div class="section_eyebrow">● DANE SAMOCHODU</div>
          <div class="vehicle_grid">
            <div class="vi">
              <span class="vi_label">Marka</span>
              <span class="vi_value">{{ orDash(car.maker) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Model</span>
              <span class="vi_value">{{ orDash(car.model) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Rok produkcji</span>
              <span class="vi_value">{{ orDash(car.year) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Przebieg</span>
              <span class="vi_value">{{ formatKm(car.mileage) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Pojemność silnika</span>
              <span class="vi_value">{{ formatCc(car.engine_volume) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Skrzynia biegów</span>
              <span class="vi_value">{{ gearLabel }}</span>
              <span class="vi_note" v-if="car.gear_type">kod JDM: {{ car.gear_type }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Liczba biegów</span>
              <span class="vi_value">{{ orDash(car.gears_count) }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Kierownica</span>
              <span class="vi_value">{{ steeringLabel }}</span>
            </div>
            <div class="vi">
              <span class="vi_label">Numer nadwozia</span>
              <span class="vi_value mono">{{ orDash(car.vin_number) }}</span>
              <span class="vi_note">japoński kod nadwozia, nie VIN</span>
            </div>
            <div class="vi vi_wide">
              <span class="vi_label">Pełna nazwa z aukcji</span>
              <span class="vi_value">{{ orDash(car.title) }}</span>
            </div>
          </div>

          <!-- Auction sheet -->
          <div class="section_eyebrow">● ARKUSZ AUKCYJNY</div>
          <div class="sheet_box">
            <template v-if="car.spec_image">
              <button class="sheet_trigger" @click="openSheet" aria-label="Powiększ arkusz aukcyjny">
                <img :src="car.spec_image" @error="imageError" class="sheet_img" />
              </button>

              <div class="legend">
                <div class="legend_title">Jak czytać oznaczenia</div>
                <div class="legend_group" v-for="section in sheetLegend" :key="section.group">
                  <div class="legend_group_name">{{ section.group }}</div>
                  <dl class="legend_list">
                    <div class="legend_item" v-for="(row, i) in section.items" :key="section.group + i">
                      <dt class="legend_code">{{ row[0] }}</dt>
                      <dd class="legend_desc">{{ row[1] }}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </template>
            <p v-else class="sheet_empty">Brak arkusza aukcyjnego dla tej aukcji.</p>
          </div>
        </div>

        <!-- Right: auction card + kalkulator -->
        <div class="right_col">
          <div class="auction_card">
            <div class="card_eyebrow">● AUKCJA</div>

            <!-- Cena wywoławcza: brak u ~15% aut, wtedy "na zapytanie" -->
            <div class="price_block">
              <div class="price_label">Cena wywoławcza</div>
              <div class="price_value" v-if="price">{{ price }}</div>
              <div class="price_missing" v-else>Cena na zapytanie</div>
              <div class="price_note" v-if="price">kwota w jenach, bez kosztów importu</div>
            </div>

            <!-- Ocena stanu -->
            <div class="rating_block" :class="{ rating_warn: repaired }">
              <div class="rating_head">
                <span class="rating_score">{{ orDash(car.auction_rating) }}</span>
                <span class="rating_text">{{ ratingLabel }}</span>
              </div>
              <p class="rating_alert" v-if="repaired">
                Auto ma w arkuszu aukcyjnym adnotację o naprawie powypadkowej.
              </p>
            </div>

            <div class="card_divider"></div>
            <div class="auction_times">
              <div class="time_block">
                <div class="time_label">Data aukcji</div>
                <div class="time_value">{{ auctionDate }}</div>
              </div>
              <div class="time_block">
                <div class="time_label">Status</div>
                <div class="time_countdown" :class="ended ? 'ended' : 'live'">
                  {{ countdown }}
                </div>
              </div>
            </div>
            <div class="card_divider"></div>
            <div class="info_row">
              <span class="row_label">Dom aukcyjny</span>
              <span class="row_value">{{ orDash(car.auction_location) }}</span>
            </div>
            <div class="info_row">
              <span class="row_label">Numer LOT</span>
              <span class="row_value">{{ orDash(car.lot) }}</span>
            </div>
            <div class="info_row">
              <span class="row_label">Numer nadwozia</span>
              <span class="row_value mono">{{ orDash(car.vin_number) }}</span>
            </div>
            <div class="card_cta">
              <button class="cta_button">Zapytaj o auto →</button>
            </div>
            <div class="card_divider" style="margin-top: 24px;"></div>
            <div class="contact_section">
              <div class="contact_eyebrow">● DORADCA HANDLOWY</div>
              <div class="contact_placeholder">
                <div class="contact_info">
                  <div class="contact_detail">Tel. 509 230 500</div>
                </div>
              </div>
              <a class="contact_button" href="tel:+48509230500">Zadzwoń do doradcy</a>
            </div>
          </div>

          <!-- Kalkulator importu — te same stawki co na stronie /kalkulator/ -->
          <div class="calc_card">
            <div class="card_eyebrow">● KALKULATOR IMPORTU</div>

            <div class="calc_input">
              <label for="calc-price">Cena samochodu na aukcji</label>
              <span class="calc_field">
                <input
                  id="calc-price"
                  v-model.number="calcPrice"
                  type="number"
                  min="0"
                  step="10000"
                />
                <span class="calc_currency">JPY</span>
              </span>
            </div>

            <p class="calc_hint">
              <template v-if="car.start_price">
                Wstępnie wpisana cena wywoławcza tej aukcji. Zmień kwotę, aby policzyć
                koszt dla własnej oferty.
              </template>
              <template v-else>
                Ta aukcja nie ma podanej ceny wywoławczej — wpisz szacowaną kwotę
                w jenach (JPY).
              </template>
            </p>

            <table class="calc_summary">
              <tbody>
                <tr>
                  <th scope="row">1. Opłaty aukcyjne</th>
                  <td>
                    <span class="calc_pln">{{ pln(auctionFee * calcRate) }}</span>
                    <span class="calc_jpy">{{ jpy(auctionFee) }}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">2. Opłata celna</th>
                  <td>
                    <span class="calc_pln">{{ pln(customs * calcRate) }}</span>
                    <span class="calc_jpy">{{ jpy(customs) }}</span>
                  </td>
                </tr>
                <tr>
                  <th scope="row">3. Nasza prowizja</th>
                  <td><span class="calc_pln">{{ pln(COMMISSION_PLN) }}</span></td>
                </tr>
                <tr class="calc_total">
                  <th scope="row">Łączny koszt samochodu</th>
                  <td><span class="calc_pln">{{ pln(total) }}</span></td>
                </tr>
              </tbody>
            </table>

            <p class="calc_rate">
              <template v-if="rate">
                Kurs przeliczenia: <strong>1 JPY = {{ rate.toFixed(4) }} PLN</strong>
                (źródło: NBP, tabela A z dnia {{ rateDate }}). Kurs ma charakter
                informacyjny i może się różnić od kursu transakcyjnego.
              </template>
              <template v-else>
                Nie udało się pobrać kursu z NBP. Użyto kursu zastępczego:
                <strong>1 JPY = {{ FALLBACK_RATE.toFixed(4) }} PLN</strong>.
              </template>
            </p>
          </div>
        </div>
      </div>

      <!-- Powiększony arkusz aukcyjny -->
      <div class="lightbox" v-if="sheetOpen" @click="closeSheet">
        <button class="lightbox_close" @click="closeSheet" aria-label="Zamknij">&times;</button>
        <img :src="car.spec_image" class="lightbox_img" @click.stop />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { goToResults } from "@/utils/embed";
import {
  PLACEHOLDER,
  orDash,
  gearLabel,
  ratingLabel,
  isRepaired,
  steeringLabel,
  formatPrice,
  formatKm,
  formatCc,
  formatAuctionDate,
  auctionCountdown,
  auctionEnded,
  galleryPhotos,
  SHEET_LEGEND,
} from "@/utils/jdm";
import {
  FALLBACK_RATE,
  COMMISSION_PLN,
  auctionFee,
  customsFee,
  totalPln,
  pln,
  jpy,
  fetchJpyRate,
} from "@/utils/importCost";

export default {
  name: "AuctionView",
  props: {
    // Set when the view is rendered inside /wyniki-wyszukiwania/ rather than
    // reached through the /auction/:id route.
    auctionId: { type: String, default: null },
  },
  data() {
    return {
      car: null,
      loaded: false,
      error: null,
      currentIndex: 0,
      sheetOpen: false,
      sheetLegend: SHEET_LEGEND,
      // Kalkulator importu — cena wywoławcza z aukcji jest w jenach, więc
      // wchodzi do wyliczeń bez przewalutowania.
      FALLBACK_RATE,
      COMMISSION_PLN,
      calcPrice: 0,
      rate: null,
      rateDate: "",
    };
  },
  created() {
    this.fetchAuction();
    this.fetchRate();
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },
  computed: {
    photos() {
      return galleryPhotos(this.car);
    },
    gearLabel() {
      return gearLabel(this.car?.gear_type);
    },
    ratingLabel() {
      return ratingLabel(this.car?.auction_rating);
    },
    repaired() {
      return isRepaired(this.car?.auction_rating);
    },
    steeringLabel() {
      return steeringLabel(this.car?.steering_wheel);
    },
    price() {
      return formatPrice(this.car?.start_price);
    },
    auctionDate() {
      return formatAuctionDate(this.car?.auction_date);
    },
    countdown() {
      return auctionCountdown(this.car?.auction_date);
    },
    ended() {
      return auctionEnded(this.car?.auction_date);
    },
    // Kurs użyty do przeliczeń — NBP, a gdy niedostępny, zastępczy.
    calcRate() {
      return this.rate || FALLBACK_RATE;
    },
    auctionFee() {
      return auctionFee(this.calcPrice);
    },
    customs() {
      return customsFee(this.calcPrice);
    },
    total() {
      return totalPln(this.calcPrice, this.rate);
    },
  },
  methods: {
    // Wystawione dla szablonu — Options API nie widzi importów bezpośrednio.
    orDash,
    formatKm,
    formatCc,
    pln,
    jpy,
    async fetchRate() {
      const { rate, date } = await fetchJpyRate();
      this.rate = rate;
      this.rateDate = date;
    },
    async fetchAuction() {
      const id = this.auctionId || this.$route.params.id;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/japan/get-auction-by-id",
          { params: { id } }
        );
        if (data.error) {
          this.error = data.error;
        } else {
          this.car = data.result;
          // start_price jest w jenach — tyle samo, ile przyjmuje kalkulator.
          this.calcPrice = Number(this.car.start_price) || 0;
          this.loaded = true;
        }
      } catch {
        this.error = "Nie udało się załadować aukcji.";
      }
    },
    goBack() {
      // Wracamy do wyników, zachowując wybrane wcześniej filtry (bez numeru
      // aukcji). W iframe nawigujemy stroną nadrzędną, natywnie — routerem.
      // eslint-disable-next-line no-unused-vars
      const { lot_number, ...filters } = this.$route.query;
      goToResults(this.$router, filters);
    },
    imageError(e) {
      e.target.src = PLACEHOLDER;
    },
    openSheet() {
      this.sheetOpen = true;
      // Blokujemy przewijanie strony pod nakładką.
      document.body.style.overflow = "hidden";
    },
    closeSheet() {
      this.sheetOpen = false;
      document.body.style.overflow = "";
    },
    onKeydown(e) {
      if (e.key === "Escape" && this.sheetOpen) this.closeSheet();
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.photos.length;
    },
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.photos.length) % this.photos.length;
    },
  },
};
</script>

<style scoped>
.auction_view {
  background: #0d0d0d;
  min-height: 100vh;
  font-family: 'DM Sans', sans-serif;
  color: #fff;
  box-sizing: border-box;
}

/* Loading / error states */
.state_box {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(255, 255, 255, 0.08);
  border-top-color: #d21f1f;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error_card {
  background: #161616;
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 40px 48px;
  text-align: center;
  max-width: 480px;
}

.error_card p {
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

/* Header */
.top_header {
  background: #111;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 36px 0 28px;
}

.header_inner {
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px;
}

.badge_row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.badge_jdm {
  background: #d21f1f;
  color: #fff;
}

.badge_trans {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
}

.badge_rating {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

/* Naprawa powypadkowa (ocena R / R2) — musi rzucać się w oczy */
.badge_warn {
  background: #7a4a00;
  color: #ffcf7a;
}

h1 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 20px;
  line-height: 1.1;
}

.info_strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
}

.info_chip {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
}

.chip_label {
  margin-right: 5px;
}

.info_chip strong {
  color: rgba(255, 255, 255, 0.82);
  font-weight: 600;
}

/* Body layout */
.body {
  max-width: 1680px;
  margin: 0 auto;
  padding: 32px 40px 64px;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 28px;
  align-items: start;
}

.left_col {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* Gallery */
.gallery_wrap {
  display: flex;
  flex-direction: column;
}

.main_image_wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: #1a1a1a;
  overflow: hidden;
}

.main_img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.14);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: background 0.15s, border-color 0.15s;
  z-index: 2;
}

.arrow:hover {
  background: #d21f1f;
  border-color: transparent;
}

.arrow_l { left: 14px; }
.arrow_r { right: 14px; }

.counter {
  position: absolute;
  bottom: 12px;
  right: 14px;
  background: rgba(0, 0, 0, 0.5);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
}

.thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-top: none;
}

.thumb {
  width: 80px;
  height: 60px;
  object-fit: cover;
  cursor: pointer;
  opacity: 0.45;
  transition: opacity 0.15s, outline 0.1s;
  outline: 2px solid transparent;
}

.thumb.active,
.thumb:hover {
  opacity: 1;
  outline: 2px solid #d21f1f;
}

/* Vehicle specs section */
.section_eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #d21f1f;
}

.vehicle_grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px 16px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 24px;
}

.vi {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vi_label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.32);
}

.vi_value {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}

.vi_note {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.28);
  line-height: 1.3;
}

.vi_wide {
  grid-column: 1 / -1;
}

/* Arkusz aukcyjny */
.sheet_box {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 16px;
}

.sheet_trigger {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: zoom-in;
}

.sheet_img {
  width: 100%;
  display: block;
  background: #1a1a1a;
  transition: opacity 0.15s;
}

.sheet_trigger:hover .sheet_img {
  opacity: 0.85;
}

.sheet_empty {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

/* Legenda oznaczeń z arkusza */
.legend {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.legend_title {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 16px;
}

.legend_group + .legend_group {
  margin-top: 18px;
}

.legend_group_name {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.32);
  margin-bottom: 10px;
}

.legend_list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 24px;
  margin: 0;
}

.legend_item {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.legend_code {
  flex: 0 0 34px;
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 12px;
  font-weight: 700;
  color: #d21f1f;
}

.legend_desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.68);
}

/* Nakładka z powiększonym arkuszem */
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  cursor: zoom-out;
}

.lightbox_img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  cursor: default;
}

.lightbox_close {
  position: absolute;
  top: 18px;
  right: 24px;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
  color: #fff;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;
}

.lightbox_close:hover {
  background: #d21f1f;
  border-color: transparent;
}

/* Cena wywoławcza */
.price_block {
  margin-bottom: 18px;
}

.price_label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.32);
  margin-bottom: 6px;
}

.price_value {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
}

.price_missing {
  font-size: 18px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
}

.price_note {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 5px;
}

/* Ocena stanu */
.rating_block {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 12px 14px;
  margin-bottom: 20px;
}

.rating_block.rating_warn {
  background: rgba(122, 74, 0, 0.18);
  border-color: rgba(255, 207, 122, 0.35);
}

.rating_head {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.rating_score {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.rating_text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.rating_alert {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #ffcf7a;
}

/* Prawa kolumna: karta aukcji + kalkulator. Razem są wyższe niż ekran,
   więc kolumna nie jest przyklejona — inaczej dół kalkulatora byłby ucięty. */
.right_col {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.auction_card {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 24px;
}

.card_eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #d21f1f;
  margin-bottom: 20px;
}

.auction_times {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.time_block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.time_label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.32);
}

.time_value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 500;
  line-height: 1.4;
}

.time_countdown {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.time_countdown.live {
  color: #d21f1f;
}

.time_countdown.ended {
  color: rgba(255, 255, 255, 0.3);
}

.card_divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin-bottom: 16px;
}

.info_row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
}

.info_row:last-of-type {
  border-bottom: none;
}

.row_label {
  color: rgba(255, 255, 255, 0.38);
  font-weight: 400;
  flex-shrink: 0;
}

.row_value {
  color: rgba(255, 255, 255, 0.82);
  font-weight: 600;
  text-align: right;
  word-break: break-all;
}

.mono {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 12px;
  letter-spacing: 0.03em;
}

.card_cta {
  margin-top: 24px;
}

.cta_button {
  width: 100%;
  padding: 14px 24px;
  background: #d21f1f;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.cta_button:hover {
  background: #b81a1a;
}

.cta_button:active {
  transform: scale(0.98);
}

/* Kalkulator importu */
.calc_card {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 24px;
}

.calc_input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.calc_input label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
}

.calc_field {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.calc_field input {
  width: 130px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.calc_field input:focus {
  outline: none;
  border-color: #d21f1f;
}

.calc_currency {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.45);
}

.calc_hint,
.calc_rate {
  font-size: 11px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.3);
}

.calc_hint {
  margin: 0 0 12px;
}

.calc_rate {
  margin: 10px 0 0;
}

.calc_rate strong {
  color: rgba(255, 255, 255, 0.55);
}

.calc_summary {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.calc_summary th {
  padding: 8px 0;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
  text-align: left;
}

.calc_summary td {
  padding: 8px 0;
  text-align: right;
}

.calc_summary tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.calc_summary tr:last-child {
  border-bottom: none;
}

.calc_pln {
  display: block;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
}

.calc_jpy {
  display: block;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.28);
}

.calc_total th {
  padding-top: 12px;
  color: #fff;
  font-weight: 700;
}

.calc_total td {
  padding-top: 12px;
}

.calc_total .calc_pln {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 19px;
  font-weight: 700;
  color: #fff;
}

/* Back button */
.back_btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.5);
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 16px;
  cursor: pointer;
  border-radius: 999px;
  margin-bottom: 18px;
  transition: color 0.15s, border-color 0.15s;
}

.back_btn:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

/* Contact section */
.contact_section {
  padding-top: 4px;
}

.contact_eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 14px;
}

.contact_placeholder {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.contact_avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.contact_info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.contact_name {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}

.contact_detail {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
}

.contact_button {
  display: block;
  width: 100%;
  padding: 12px 24px;
  background: transparent;
  color: #aaaaaa;
  border: 1px solid #aaaaaa;
  border-radius: 999px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  box-sizing: border-box;
}

/* Responsive */
@media (max-width: 1000px) {
  .body {
    grid-template-columns: 1fr;
  }
  .vehicle_grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 640px) {
  .header_inner {
    padding: 0 20px;
  }
  .top_header {
    padding: 24px 0 18px;
  }
  .body {
    padding: 20px 20px 40px;
  }
  h1 {
    font-size: 26px;
  }
  .vehicle_grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 16px;
  }
  .legend_list {
    grid-template-columns: 1fr;
  }
  .lightbox {
    padding: 16px;
  }
}
</style>
